import * as React from 'react';
import { useRefEffect } from '@fluentui/react-hooks';
import {
    allowScrollOnElement,
    disableBodyScroll,
    enableBodyScroll,
    EventGroup,
    findScrollableParent,
} from '@fluentui/react';

function isHTMLElement(element: HTMLElement | Window): element is HTMLElement {
    return 'scrollHeight' in element;
}

function isScrollbarVisible(listElement: HTMLElement): boolean {
    // When the Callout is mounted, if the Callout content (i.e. SectionedList)
    // is very tall exceeding the window height, the Callout will stretch to its
    // content, before it resizes itself to fit the window and shows the scrollbar.
    // In which case, the SectionedList will not see a resize notification. Which
    // means the Callout width is set to the non-scrollbar value, instead of scrollbar one.
    // To work around that case, we do short-circuit check here. If SectionedList is
    // taller than the window, treat it as if the scrollbar is shown.
    if (listElement.clientHeight > window.innerHeight) {
        return true;
    }

    // search up the DOM tree to look for the Callout element.
    const calloutElement = findScrollableParent(listElement);
    return (
        !!calloutElement &&
        isHTMLElement(calloutElement) &&
        calloutElement.scrollHeight > calloutElement.clientHeight
    );
}

export function useCalloutWidth(
    dropDownWidth: number | undefined,
    scrollbarWidth: number | undefined
): [calloutWidth: number | undefined, sectionedListRef: React.Ref<HTMLDivElement>] {
    const [calloutWidth, setCalloutWidth] = React.useState(dropDownWidth);

    // hold the desired width of the callout if scrollbar is visible.
    const calloutScrollbarVisibleWidth = React.useMemo(() => dropDownWidth, [dropDownWidth]);

    // hold the desired width of the callout if scrollbar is hidden.
    const calloutScrollbarHiddenWidth = React.useMemo(() => {
        if (scrollbarWidth === undefined) {
            return dropDownWidth;
        }

        return dropDownWidth !== undefined
            ? Math.max(0, dropDownWidth - scrollbarWidth)
            : undefined;
    }, [dropDownWidth, scrollbarWidth]);

    const sectionedListElementRef = useRefEffect<HTMLDivElement>((listElement: HTMLDivElement) => {
        // Smart resize is not available if ResizeObserver is not supported by browser
        const observer = window.ResizeObserver
            ? new window.ResizeObserver(() => {
                  if (!calloutScrollbarVisibleWidth) {
                      return;
                  }

                  const scrollbarVisible = isScrollbarVisible(listElement);
                  setCalloutWidth(
                      scrollbarVisible ? calloutScrollbarVisibleWidth : calloutScrollbarHiddenWidth
                  );
              })
            : undefined;

        // Get notified whenever the size of the SectionedList element changes.
        observer?.observe(listElement);

        disableBodyScroll();

        allowScrollOnElement(listElement, new EventGroup(listElement));

        // Important: reenable body scroll when this control is unmounted.
        return (): void => {
            observer?.disconnect();
            enableBodyScroll();
        };
    });

    return [calloutWidth, sectionedListElementRef];
}
